import { Grid, IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { AppTable, FormContainer } from "components/molecules";
import { DownloadHelper, TableHelper } from "helpers";
import { IPaginateApiResponse } from "model/Common/IPaginateApiResponse";
import { IReceiveDetail } from "model";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReceiveActions } from "redux/actions";
import PrintIcon from "@mui/icons-material/Print";

import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { I18nTranslator, translations } from "utils/i18n";
import DownloadIcon from "@mui/icons-material/Download";
import { theme } from "themes";
import AppModal from "components/atoms/AppModal";
import { useFormik } from "formik";
import ExportPalletLabelModal, {
  IExportPalletLabel,
} from "./ExportPalletLabelModal";
import { renderProgress } from "components/atoms/PercentProgressCell";
import { ReceivingStatus } from "components/atoms";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const ReceiveDocumentDetail = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{
    stockReceivingHeaderId: string;
  }>();
  const [searchText, setSearchText] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [value, setValue] = React.useState(0);
  const [currentReceiveDetail, setCurrentReceiveDetail] =
    useState<IReceiveDetail>();
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const data = useAppSelector(
    (state) => state.ReceiveReducer.receiveDetail?.data
  ) as IPaginateApiResponse<IReceiveDetail[]>;
  const mainAction = useAppSelector((state) => state.ReceiveReducer.action);

  const action = useAppSelector(
    (state) => state.ReceiveReducer.receiveDetail?.action
  );

  const _getReceiveDocument = (pageNumber = 1) => {
    dispatch(
      ReceiveActions.getReceiveDetail.request({
        pageNumber: pageNumber,
        pageSize: 100,
        stockReceivingHeaderId: params.stockReceivingHeaderId,
        searchText: searchText,
      })
    );
  };

  const _handlePageSizeChange = (pageSize: number) => {
    dispatch(
      ReceiveActions.getReceiveDetail.request({
        pageNumber: 1,
        pageSize: pageSize,
        stockReceivingHeaderId: params.stockReceivingHeaderId,
        searchText: searchText,
      })
    );
  };

  const onFinish = () => {
    setCurrentReceiveDetail(undefined);
    setPrintModal(false);
  };

  const onOpen = (row: any) => {
    setCurrentReceiveDetail(row);
    setPrintModal(true);
  };

  const onCancelPrint = () => {
    setCurrentReceiveDetail(undefined);
    setPrintModal(false);
  };

  useEffect(() => {
    _getReceiveDocument();
  }, [searchText, refresh]);

  useEffect(() => {
    if (mainAction.includes(ReceiveActions.importReceiveDocument.successName)) {
      setRefresh(true);

      dispatch(ReceiveActions.clearAction.request());
    }
  }, [mainAction]);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "id",
      headerName: I18nTranslator(translations.receive.action),
      size: 120,
      align: "center",
      sortable: true,
      renderCell: ({ row }) => (
        <Stack direction={"row"} spacing={1}>
          <IconButton onClick={() => onOpen(row)}>
            <PrintIcon
              sx={{ color: theme.colors.primary.main }}
              fontSize="small"
            />
          </IconButton>
        </Stack>
      ),
    }),

    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productId),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.receive.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.receive.uom),
      size: "small",
    }),
    TableHelper.createCell({
      field: "batchCode",
      headerName: I18nTranslator(translations.receive.productLot),
      size: "small",
      align: "left",
    }),

    TableHelper.createCell({
      field: "status",
      headerName: I18nTranslator(translations.receive.status),
      size: "small",
      align: "left",
      renderCell: ReceivingStatus,
    }),
    TableHelper.createCell({
      field: "receivingProgress",
      headerName: I18nTranslator(translations.receive.status),
      size: "small",
      align: "center",
      renderCell: ({ row }) =>
        renderProgress((row.quantityReceived / row.quantityPlanning) * 100),
    }),
    TableHelper.createCell({
      field: "specification",
      headerName: I18nTranslator(translations.receive.palletSpecification),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "quantityPlanning",
      headerName: I18nTranslator(translations.receive.productPlanningQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "quantityReceived",
      headerName: I18nTranslator(translations.receive.productReceivedQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletPlanning",
      headerName: I18nTranslator(translations.receive.planPallet),
      size: "small",
      align: "right",
    }),
    // TableHelper.createCell({
    //   field: "palletPlanning",
    //   headerName: I18nTranslator(translations.receive.planPallet),
    //   size: "small",
    //   align: "right",
    // }),
    TableHelper.createCell({
      field: "palletReceived",
      headerName: I18nTranslator(translations.receive.actualPallet),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletSetLocation",
      headerName: I18nTranslator(translations.receive.putOnPallet),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "poCode",
      headerName: I18nTranslator(translations.receive.poNo),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "poLine",
      headerName: I18nTranslator(translations.receive.poLine),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "manufacturingDate",
      headerName: I18nTranslator(translations.receive.manufacturingDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.receive.expiredDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "note",
      headerName: I18nTranslator(translations.receive.note),
      size: "large",
      align: "left",
      editable: true,
    }),
    TableHelper.createCell({
      field: "id2",
      headerName: I18nTranslator(translations.receive.action),
      size: 120,
      align: "center",
      sortable: true,
      renderCell: ({ row }) => (
        <Stack direction={"row"} spacing={1}>
          <IconButton onClick={() => onOpen(row)}>
            <PrintIcon
              sx={{ color: theme.colors.primary.main }}
              fontSize="small"
            />
          </IconButton>
        </Stack>
      ),
    }),
  ];

  const formik = useFormik({
    initialValues: {
      quantity: 0,
      labelQuantity: 0,
    },
    onSubmit: () => {},
  });

  return (
    <>
      <AppTable<IReceiveDetail>
        columns={COLUMNS}
        loading={mainAction.includes(
          ReceiveActions.getReceiveDetail.requestName
        )}
        data={data}
        onSearch={setSearchText}
        searchPlaceholder={"Tìm kiếm: Mã lô, mã sản phẩm, tên sản phẩm"}
        onChangePage={(pageNumber) => _getReceiveDocument(pageNumber)}
        removeActionColumn={true}
        hideHeader
        defaultHeaderTable={350}
        onPageSizeChange={_handlePageSizeChange}
      />
      <ExportPalletLabelModal
        currentReceiveDetail={currentReceiveDetail}
        open={printModal}
        onClose={onCancelPrint}
        onFinish={onFinish}
      />
    </>
  );
};

export default ReceiveDocumentDetail;
